import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { isStandalone } from "@/lib/pwa";

/**
 * PwaInstallPrompt — non-intrusive "Add to home screen" CTA.
 *
 * Browsers fire a ``beforeinstallprompt`` event when the page meets the
 * install criteria. We stash it and render a small bottom banner that
 * the user can dismiss; once dismissed we don't ask again for 14 days.
 *
 * The banner hides itself on full-canvas editor routes
 * (`/seatingmaker/edit/*`, `/tickets/venue/*`) where floating chrome
 * covers the right-hand property-panel submit buttons.
 */
const DISMISS_KEY = "pwa-install-dismissed-at";
const NUDGE_AFTER_MS = 14 * 24 * 60 * 60 * 1000;
const HIDE_ON_ROUTES = [
  /^\/seatingmaker\/edit\//,
  /^\/tickets\/venue\//,
];

function _pathMatchesHide(pathname) {
  return HIDE_ON_ROUTES.some((re) => re.test(pathname || ""));
}

export default function PwaInstallPrompt() {
  const [event, setEvent] = useState(null);
  const [show, setShow] = useState(false);
  const [hiddenByRoute, setHiddenByRoute] = useState(
    () => typeof window !== "undefined"
             && _pathMatchesHide(window.location.pathname));

  useEffect(() => {
    // React to client-side route changes without pulling in useLocation
    // (this component lives outside the BrowserRouter).
    const check = () => setHiddenByRoute(_pathMatchesHide(window.location.pathname));
    window.addEventListener("popstate", check);
    window.addEventListener("pushstate", check);
    const orig = window.history.pushState;
    window.history.pushState = function (...args) {
      const r = orig.apply(this, args);
      check();
      return r;
    };
    return () => {
      window.removeEventListener("popstate", check);
      window.removeEventListener("pushstate", check);
      window.history.pushState = orig;
    };
  }, []);

  useEffect(() => {
    if (isStandalone()) return;
    const dismissedAt = parseInt(window.localStorage.getItem(DISMISS_KEY) || "0", 10);
    if (Date.now() - dismissedAt < NUDGE_AFTER_MS) return;

    function onPrompt(e) {
      e.preventDefault();
      setEvent(e);
      setShow(true);
    }
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  async function handleInstall() {
    if (!event) return;
    event.prompt();
    await event.userChoice;
    setShow(false);
  }
  function handleDismiss() {
    setShow(false);
    window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
  }

  if (!show || hiddenByRoute) return null;
  return (
    <div data-testid="pwa-install-prompt" className="fixed bottom-40 left-4 right-4 md:left-auto md:right-4 md:max-w-sm z-[9100] bg-white border border-[#E8E5DF] rounded-2xl p-4 pr-9 shadow-2xl flex items-center gap-3">
      <button
        type="button"
        onClick={handleDismiss}
        data-testid="pwa-install-dismiss"
        aria-label="Dismiss install prompt"
        className="absolute top-1.5 right-1.5 text-[#5C625E] hover:text-[#121614] bg-white hover:bg-[#F5F1EB] rounded-full w-7 h-7 flex items-center justify-center shadow-sm border border-[#E8E5DF]"
      >
        <X className="h-4 w-4" />
      </button>
      <div className="h-10 w-10 rounded-xl bg-[#C05746] text-white flex items-center justify-center flex-shrink-0">
        <Download className="h-5 w-5" strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-[#121614]">Add to home screen</p>
        <p className="text-xs text-[#5C625E]">Faster bookings + works offline.</p>
      </div>
      <button
        type="button"
        onClick={handleInstall}
        data-testid="pwa-install-confirm"
        className="text-sm font-semibold text-white bg-[#C05746] hover:bg-[#A64A3C] px-4 py-2 rounded-full whitespace-nowrap"
      >
        Install
      </button>
    </div>
  );
}
